import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Download, AlertTriangle, Check, Edit2, X, FileText, RefreshCw, ArrowLeft } from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';

// RACI color configuration
const raciColors = {
  R: { bg: '#E8F5E9', text: '#2E7D32', label: 'Responsible' },
  A: { bg: '#E3F2FD', text: '#1565C0', label: 'Accountable' },
  C: { bg: '#FFF3E0', text: '#E65100', label: 'Consulted' },
  I: { bg: '#F3E5F5', text: '#7B1FA2', label: 'Informed' }
};

interface ProcessStep {
  id: string;
  name: string;
  phaseId: string;
}

interface Phase {
  id: string;
  name: string;
  expanded: boolean;
  steps: ProcessStep[];
}

interface Entity {
  id: string;
  name: string;
  abbrev: string;
}

interface RACIAssignments {
  [stepId: string]: {
    [entityId: string]: string[]; // ['R', 'A', 'C', 'I']
  };
}

// Get combined background for multi-code cells
const getCellStyle = (codes: string[]) => {
  if (!codes || codes.length === 0) return { bg: '#FFFFFF', text: '#9E9E9E' };
  if (codes.length === 1) return { bg: raciColors[codes[0] as keyof typeof raciColors].bg, text: raciColors[codes[0] as keyof typeof raciColors].text };
  // For combinations, use highest priority color (A > R > C > I)
  const priority = ['A', 'R', 'C', 'I'];
  const highest = priority.find(p => codes.includes(p));
  return { bg: raciColors[highest as keyof typeof raciColors].bg, text: '#424242' };
};

const CellEditor: React.FC<{
  stepId: string;
  entityId: string;
  currentCodes: string[];
  onSave: (codes: string[]) => void;
  onClose: () => void;
}> = ({ stepId, entityId, currentCodes, onSave, onClose }) => {
  const [selected, setSelected] = useState(new Set(currentCodes));

  const toggleCode = (code: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(code)) {
      newSelected.delete(code);
    } else {
      newSelected.add(code);
    }
    setSelected(newSelected);
  };

  return (
    <div className="absolute z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-3 min-w-48"
         style={{ top: '100%', left: '50%', transform: 'translateX(-50%)' }}>
      <div className="text-xs font-medium text-gray-500 mb-2">Select RACI codes:</div>
      <div className="space-y-1">
        {['R', 'A', 'C', 'I'].map(code => (
          <label key={code}
                 className="flex items-center gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={selected.has(code)}
              onChange={() => toggleCode(code)}
              className="w-4 h-4 rounded"
            />
            <span className="font-bold text-sm" style={{ color: raciColors[code as keyof typeof raciColors].text }}>
              {code}
            </span>
            <span className="text-xs text-gray-500">— {raciColors[code as keyof typeof raciColors].label}</span>
          </label>
        ))}
      </div>
      <div className="flex gap-2 mt-3 pt-2 border-t">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSave(Array.from(selected).sort());
          }}
          className="flex-1 bg-blue-600 text-white text-xs py-1.5 rounded hover:bg-blue-700 flex items-center justify-center gap-1">
          <Check size={12} /> Save
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="flex-1 bg-gray-100 text-gray-700 text-xs py-1.5 rounded hover:bg-gray-200 flex items-center justify-center gap-1">
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
};

const RACIMatrix: React.FC = () => {
  const { currentProject } = useProject();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'businessUnit' | 'stakeholderRole'>('businessUnit');
  const [phases, setPhases] = useState<Phase[]>([]);
  const [businessUnits, setBusinessUnits] = useState<Entity[]>([]);
  const [stakeholderRoles, setStakeholderRoles] = useState<Entity[]>([]);
  const [assignments, setAssignments] = useState<{ businessUnit: RACIAssignments; stakeholderRole: RACIAssignments }>({
    businessUnit: {},
    stakeholderRole: {}
  });
  const [editingCell, setEditingCell] = useState<{ stepId: string; entityId: string } | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [autoGenerated, setAutoGenerated] = useState(false);
  const [stakeholderRolesGenerating, setStakeholderRolesGenerating] = useState(false);

  const columns = viewMode === 'businessUnit' ? businessUnits : stakeholderRoles;

  useEffect(() => {
    if (currentProject) {
      loadRACIData();
    }
  }, [currentProject]);

  // Check if stakeholder roles need loading whenever view changes or data updates
  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;
    let timeout: NodeJS.Timeout | null = null;

    if (viewMode === 'stakeholderRole' && !loading && !generating) {
      const hasStakeholderRoles = stakeholderRoles.length > 0;
      const hasStakeholderAssignments = Object.keys(assignments.stakeholderRole).length > 0;
      const hasPhases = phases.length > 0;

      // If we have stakeholder roles and phases but no assignments, show loading
      if (hasStakeholderRoles && hasPhases && !hasStakeholderAssignments && !stakeholderRolesGenerating) {
        console.log('🔍 Detected missing stakeholder role assignments - showing loading state');
        setStakeholderRolesGenerating(true);

        // Poll for assignments
        pollInterval = setInterval(async () => {
          try {
            console.log('🔄 Polling for stakeholder role assignments...');
            const response = await api.get(`/api/raci/${currentProject?.id}`);
            if (response.data.success) {
              const srAssignments = response.data.assignments?.stakeholderRole || {};
              const hasAssignments = Object.keys(srAssignments).length > 0;

              console.log(`📊 Poll result: ${hasAssignments ? `Found ${Object.keys(srAssignments).length} steps with assignments` : 'No assignments yet'}`);

              if (hasAssignments) {
                console.log('✅ Stakeholder role assignments loaded - updating display');
                // Update ALL data to ensure everything is in sync
                setPhases(response.data.phases || []);
                setBusinessUnits(response.data.businessUnits || []);
                setStakeholderRoles(response.data.stakeholderRoles || []);
                setAssignments(response.data.assignments || { businessUnit: {}, stakeholderRole: {} });
                setStakeholderRolesGenerating(false);
                if (pollInterval) clearInterval(pollInterval);
                toast.success('Stakeholder role assignments loaded successfully!', { duration: 3000 });
              } else {
                console.log('⏳ Still waiting for assignments...');
              }
            }
          } catch (err) {
            console.error('❌ Error polling for assignments:', err);
            setStakeholderRolesGenerating(false);
            if (pollInterval) clearInterval(pollInterval);
            toast.error('Failed to load stakeholder role assignments');
          }
        }, 2000); // Poll every 2 seconds

        // Stop polling after 30 seconds
        timeout = setTimeout(() => {
          if (pollInterval) clearInterval(pollInterval);
          setStakeholderRolesGenerating(false);
          console.log('⏱️ Polling timeout - stakeholder role assignments not loaded after 30 seconds');
          toast.error('Stakeholder role generation timed out. Please try clicking "Generate RACI" button.', { duration: 5000 });
        }, 30000);
      }
    }

    // Cleanup function
    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (timeout) clearTimeout(timeout);
    };
  }, [viewMode, stakeholderRoles, assignments, phases, loading, generating, currentProject]);

  // Auto-generate ONLY when: (1) Coming from SOP Builder with ?autoGenerate=true AND (2) No existing data
  useEffect(() => {
    const shouldAutoGenerate = searchParams.get('autoGenerate') === 'true';
    const hasNoData = Object.keys(assignments.businessUnit).length === 0 &&
                      Object.keys(assignments.stakeholderRole).length === 0;

    if (shouldAutoGenerate && !loading && !autoGenerated && currentProject && hasNoData && phases.length > 0) {
      console.log('🤖 Auto-generating RACI from SOP Builder (no existing data)');
      setAutoGenerated(true);
      setGenerating(true); // Set generating state immediately to show loading
      generateWithAI();
    } else if (shouldAutoGenerate && !hasNoData) {
      console.log('📊 Showing existing RACI data (already generated)');
    }
  }, [loading, currentProject, assignments, autoGenerated, phases]);

  const loadRACIData = async () => {
    if (!currentProject) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/raci/${currentProject.id}`);

      if (response.data.success) {
        setPhases(response.data.phases || []);
        setBusinessUnits(response.data.businessUnits || []);
        setStakeholderRoles(response.data.stakeholderRoles || []);
        setAssignments(response.data.assignments || { businessUnit: {}, stakeholderRole: {} });

        // The useEffect will handle loading state detection
      }
    } catch (error: any) {
      console.error('Failed to load RACI data:', error);
      if (error.response?.status === 404) {
        // No RACI data exists yet, that's okay
        toast.error('No RACI data found. Please generate first.');
      } else {
        toast.error('Failed to load RACI data');
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePhase = (phaseId: string) => {
    setPhases(phases.map(p =>
      p.id === phaseId ? { ...p, expanded: !p.expanded } : p
    ));
  };

  const getAssignment = (stepId: string, entityId: string) => {
    return assignments[viewMode][stepId]?.[entityId] || [];
  };

  const updateAssignment = async (stepId: string, entityId: string, newCodes: string[]) => {
    const newAssignments = {
      ...assignments,
      [viewMode]: {
        ...assignments[viewMode],
        [stepId]: {
          ...assignments[viewMode][stepId],
          [entityId]: newCodes
        }
      }
    };

    setAssignments(newAssignments);
    setEditingCell(null);

    // Save to backend
    try {
      await api.put(`/api/raci/${currentProject?.id}`, {
        viewMode,
        stepId,
        entityId,
        codes: newCodes
      });
      toast.success('RACI assignment updated');
    } catch (error) {
      console.error('Failed to update RACI assignment:', error);
      toast.error('Failed to save changes');
      // Revert on error
      setAssignments(assignments);
    }
  };

  // Validation: check for steps without Accountable
  const getValidationIssues = () => {
    const issues: any[] = [];
    phases.forEach(phase => {
      phase.steps.forEach(step => {
        const stepAssignments = assignments[viewMode][step.id] || {};
        const hasAccountable = Object.values(stepAssignments).some(codes => codes.includes('A'));
        if (!hasAccountable) {
          issues.push({ step: step.name, phase: phase.name, type: 'missing_accountable' });
        }
      });
    });
    return issues;
  };

  const validationIssues = getValidationIssues();

  const generateWithAI = async () => {
    if (!currentProject) return;

    try {
      setGenerating(true);
      setStakeholderRolesGenerating(false); // Clear any previous stakeholder generating state
      toast.loading('Regenerating RACI matrix with AI...', { id: 'generating' });

      const response = await api.post(`/api/raci/${currentProject.id}/generate`);

      if (response.data.success) {
        const { assignmentsCount, deletedCount } = response.data;
        toast.success(
          `Deleted ${deletedCount || 0} old assignments, generated ${assignmentsCount} new assignments!`,
          { id: 'generating', duration: 4000 }
        );
        // Reload RACI data to show new assignments
        await loadRACIData();
      } else {
        toast.error('Generation failed', { id: 'generating' });
      }
    } catch (error: any) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.message || 'Failed to regenerate RACI matrix', { id: 'generating' });
    } finally {
      setGenerating(false);
      setStakeholderRolesGenerating(false); // Ensure stakeholder generating state is cleared
    }
  };

  const exportToExcel = async () => {
    if (!currentProject) return;

    try {
      setExporting(true);
      const response = await api.get(`/api/raci/${currentProject.id}/export`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `RACI-Matrix-${currentProject.name}-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('RACI Matrix exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export RACI Matrix');
    } finally {
      setExporting(false);
    }
  };

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Please select a project to use RACI Matrix</p>
        </div>
      </div>
    );
  }

  if (loading || generating) {
    return (
      <div className="space-y-6">
        {/* Back Button */}
        <div className="flex justify-end">
          <button
            onClick={() => navigate('/app/sop-builder')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to SOP Builder</span>
          </button>
        </div>

        {/* Loading State */}
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg font-medium">{generating ? 'Generating RACI Matrix...' : 'Loading RACI Matrix...'}</p>
            <p className="text-gray-500 text-sm mt-2">This may take a few moments</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-super-dark-purple">RACI Matrix</h1>
          <p className="text-gray-600">Responsible, Accountable, Consulted, Informed</p>
        </div>
        <div className="flex gap-3">
          {/* Back Button */}
          <button
            onClick={() => navigate('/app/sop-builder')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          {/* Regenerate RACI button - Hidden for now
          <button
            onClick={generateWithAI}
            disabled={generating || phases.length === 0}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {generating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Regenerating...</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>Regenerate RACI</span>
              </>
            )}
          </button>
          */}
          {/* Edit Mode Toggle */}
          <button
            onClick={() => setEditMode(!editMode)}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
              editMode
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <Edit2 className="w-4 h-4" />
            <span>{editMode ? 'Edit Mode: ON' : 'Edit Mode: OFF'}</span>
          </button>
          <button
            onClick={exportToExcel}
            disabled={exporting || phases.length === 0}
            className="bg-electric-blue text-white px-4 py-2 rounded-lg hover:bg-darker-blue transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span>Export to Excel</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">View by:</span>
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('businessUnit')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'businessUnit'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Business Units
            </button>
            <button
              onClick={() => setViewMode('stakeholderRole')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                viewMode === 'stakeholderRole'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span>Stakeholder Roles</span>
              {stakeholderRolesGenerating && (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Edit Mode Banner */}
      {editMode && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Edit2 className="w-5 h-5 text-green-600" />
            <div>
              <h4 className="font-semibold text-green-900">Edit Mode Active</h4>
              <p className="text-sm text-green-800">
                Click on any cell to edit RACI assignments. Turn off edit mode when done to prevent accidental changes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Validation Warnings */}
      {validationIssues.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-900 mb-1">Validation Issues</h4>
              <p className="text-sm text-amber-800 mb-2">
                {validationIssues.length} step(s) missing an Accountable person:
              </p>
              <ul className="text-sm text-amber-800 list-disc list-inside">
                {validationIssues.slice(0, 5).map((issue, idx) => (
                  <li key={idx}>{issue.phase} → {issue.step}</li>
                ))}
                {validationIssues.length > 5 && (
                  <li className="text-amber-600">...and {validationIssues.length - 5} more</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* RACI Legend */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-800 mb-3">RACI Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(raciColors).map(([code, config]) => (
            <div key={code} className="flex items-center space-x-2">
              <div
                className="w-8 h-8 rounded flex items-center justify-center font-bold text-sm"
                style={{ backgroundColor: config.bg, color: config.text }}
              >
                {code}
              </div>
              <span className="text-sm text-gray-700">{config.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* RACI Grid */}
      {phases.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">No Process Data Available</h3>
          <p className="text-gray-600">Please generate process maps or SOPs first.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#221551] text-white">
                  <th className="sticky left-0 z-20 bg-[#221551] px-4 py-3 text-left font-semibold min-w-64">
                    Process Steps
                  </th>
                  {columns.map(entity => (
                    <th key={entity.id} className="px-3 py-3 text-center font-semibold min-w-32">
                      <div className="font-bold">{entity.abbrev}</div>
                      <div className="text-xs font-normal opacity-90">{entity.name}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {phases.map(phase => (
                  <React.Fragment key={phase.id}>
                    {/* Phase Row */}
                    <tr className="bg-gray-100 border-t border-gray-300">
                      <td
                        colSpan={columns.length + 1}
                        className="sticky left-0 z-10 px-4 py-3 font-semibold text-gray-900 cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => togglePhase(phase.id)}
                      >
                        <div className="flex items-center space-x-2">
                          {phase.expanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          <span>{phase.name}</span>
                          <span className="text-xs text-gray-600">({phase.steps.length} steps)</span>
                        </div>
                      </td>
                    </tr>

                    {/* Step Rows */}
                    {phase.expanded && phase.steps.map(step => (
                      <tr key={step.id} className="border-t border-gray-200 hover:bg-gray-50">
                        <td className="sticky left-0 z-10 bg-white px-4 py-2 text-gray-900 border-r border-gray-200">
                          {step.name}
                        </td>
                        {columns.map(entity => {
                          const codes = getAssignment(step.id, entity.id);
                          const cellStyle = getCellStyle(codes);
                          const isEditing = editingCell?.stepId === step.id && editingCell?.entityId === entity.id;

                          // Show loading state when:
                          // 1. Generate RACI button clicked (generating = true) for ALL cells
                          // 2. Stakeholder roles auto-generating (stakeholderRolesGenerating = true) for ALL cells (not just stakeholder view)
                          // 3. Process map auto-sync happening (regenerating all assignments)
                          const isGenerating = (generating || stakeholderRolesGenerating) && codes.length === 0;

                          return (
                            <td
                              key={entity.id}
                              className={`px-3 py-2 text-center relative transition-all ${
                                editMode && !isGenerating
                                  ? 'cursor-pointer hover:ring-2 hover:ring-green-400 hover:shadow-md'
                                  : 'cursor-default'
                              }`}
                              style={{ backgroundColor: isGenerating ? '#F3F4F6' : cellStyle.bg }}
                              onClick={() => editMode && !isGenerating && setEditingCell({ stepId: step.id, entityId: entity.id })}
                            >
                              {isGenerating ? (
                                <div className="flex items-center justify-center gap-1">
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600"></div>
                                  <span className="text-xs text-gray-500">Loading...</span>
                                </div>
                              ) : (
                                <div
                                  className="font-bold text-sm"
                                  style={{ color: cellStyle.text }}
                                >
                                  {codes.length > 0 ? codes.join(', ') : '—'}
                                </div>
                              )}

                              {isEditing && !isGenerating && editMode && (
                                <CellEditor
                                  stepId={step.id}
                                  entityId={entity.id}
                                  currentCodes={codes}
                                  onSave={(newCodes) => updateAssignment(step.id, entity.id, newCodes)}
                                  onClose={() => setEditingCell(null)}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RACIMatrix;
